import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { motion } from "framer-motion";
import { panel } from "../motion";
import { hkToday } from "../lib/dates";
import { IconChevronLeft, IconChevronRight } from "./icons";
import { fmtCell, MONTHS, monthCells, WEEKDAYS } from "./calendar-grid";

export interface DateRange {
  start: string | null; // YYYY-MM-DD
  end: string | null; // YYYY-MM-DD, or null while only the start is picked (→ now)
}

interface Anchor {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface RangeCalendarProps {
  start: string | null;
  end: string | null;
  /** CSS colour (e.g. "var(--warn)") used to paint the range bar. */
  color: string;
  onChange: (range: DateRange) => void;
  /** Extra content rendered above the month grid inside the popup (e.g. a status picker). */
  header?: ReactNode;
  /** Extra content rendered in the popup footer below Today/Clear (e.g. Save/Cancel). */
  actions?: ReactNode;
  /** Single-day mode: one click picks the day (start = end). Used for
   *  discharge records, which are a single date, not a period. */
  singleDay?: boolean;
  /** The button that opened the popup — the popover is anchored to this element. */
  anchorEl: HTMLElement | null;
  /** Called when the popup dismisses itself (outside click / Escape / scroll / resize). */
  onClose: () => void;
}

/** Whole days between two YYYY-MM-DD dates (b - a; can be negative). */
const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

// Popover geometry — 8px gap from the trigger, 12px gutter from the viewport
// edges (same constants as RotaDayMenu).
const GAP = 8;
const GUTTER = 12;

/**
 * Popover calendar for picking a date range (start + end day). It opens from
 * the button that spawned it (the caller passes that element via `anchorEl`),
 * so the form action itself opens the picker instead of a separate "select
 * dates" field. Fixed-position (escapes the quick-view modal's clip), then
 * clamped to the viewport once mounted: it stays left-aligned to the trigger
 * unless it would cross the right edge (then right-aligns to it) and flips
 * above the trigger when there isn't room below. Callers inject a status
 * picker via `header` and Save/Cancel via `actions`; `onClose` lets the caller
 * reset form state when the popup dismisses itself.
 */
export function RangeCalendar({ start, end, color, onChange, header, actions, singleDay = false, anchorEl, onClose }: RangeCalendarProps) {
  const today = hkToday();
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState(() => {
    const base = start ?? today;
    const [y, m] = base.split("-").map(Number);
    return { year: y!, month: m! - 1 };
  });

  // Capture the trigger's screen rect whenever the anchor element changes so
  // the popup opens at the button that spawned it.
  useLayoutEffect(() => {
    if (!anchorEl) {
      setAnchor(null);
      setPos(null);
      return;
    }
    const r = anchorEl.getBoundingClientRect();
    setAnchor({ left: r.left, top: r.top, right: r.right, bottom: r.bottom });
  }, [anchorEl]);

  // Clamp the popup inside the viewport once mounted: prefer below-left, flip
  // right-aligned when it would cross the right edge, flip above when there
  // isn't room below. Mirrors RotaDayMenu's popover placement. Re-run on
  // resize so a viewport change (e.g. the tablet soft keyboard opening while
  // typing in the popup's search box) repositions the popup instead of
  // discarding the form.
  const computePos = useCallback((anchor: Anchor | null) => {
    if (!anchor) return null;
    const el = popRef.current;
    if (!el) return null;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let left = anchor.left;
    if (left + w > window.innerWidth - GUTTER) left = anchor.right - w;
    let top = anchor.bottom + GAP;
    if (top + h > window.innerHeight - GUTTER) top = anchor.top - GAP - h;
    return { left: Math.max(GUTTER, left), top: Math.max(GUTTER, top) };
  }, []);

  useEffect(() => {
    setPos(computePos(anchor));
  }, [anchor, computePos]);

  // Close on outside click / Escape / scroll. The outside-click
  // listener runs on `click` in the capture phase and stops the event before
  // it reaches the quick-view backdrop, so a backdrop click dismisses only
  // this popover — the quick view underneath stays (layers close one at a
  // time, not all at once). The quick-view modal scrolls internally, so the
  // scroll listener uses capture to catch that too.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (
        popRef.current &&
        !popRef.current.contains(e.target as Node) &&
        !(anchorEl && anchorEl.contains(e.target as Node))
      ) {
        e.stopPropagation();
        onClose();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.stopPropagation(); // keep the quick-view modal open; only close the popup
      onClose();
    }
    document.addEventListener("click", onDocClick, true);
    document.addEventListener("keydown", onKey);
    function onScroll(e: Event) {
      // Ignore the popup's own inner scroll (max-height + overflow) so scrolling
      // its content to reach the actions does not dismiss it.
      const t = e.target as Node | null;
      if (t && popRef.current && popRef.current.contains(t)) return;
      // A layout-viewport scroll (target document/window) leaves the fixed
      // popup and its fixed anchor exactly where they are — on Android the
      // soft keyboard fires exactly this while typing in the popup's search
      // box. Only real container scrolls (e.g. the quick-view modal body)
      // move the anchor under the popup, so those still dismiss it.
      if (t === document || (document.scrollingElement && t === document.scrollingElement)) return;
      onClose();
    }
    function onResize() {
      // Viewport changes (soft keyboard / orientation) re-clamp instead of
      // closing: the popup holds form state (status, picked dates, a typed
      // custom status) and losing it on a viewport change is worse than
      // repositioning.
      setPos(computePos(anchor));
    }
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("click", onDocClick, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [anchorEl, anchor, computePos, onClose]);

  const cells = monthCells(view.year, view.month);

  const shiftMonth = (delta: number) =>
    setView((v) => {
      const month = v.month + delta;
      if (month < 0) return { year: v.year - 1, month: 11 };
      if (month > 11) return { year: v.year + 1, month: 0 };
      return { ...v, month };
    });

  function pick(date: string) {
    if (singleDay) {
      onChange({ start: date, end: date }); // discharge: one click selects the day
      return;
    }
    if (start === null || end !== null) {
      onChange({ start: date, end: null }); // first click, or restart a complete range
    } else if (date < start) {
      onChange({ start: date, end: start }); // clicked before the start -> swap
    } else if (date > start) {
      onChange({ start, end: date });
    }
    // clicking the start day again leaves the single-day (→ now) selection
  }

  const goToday = () => {
    const [y, m] = today.split("-").map(Number);
    setView({ year: y!, month: m! - 1 });
    onChange(singleDay ? { start: today, end: today } : { start: today, end: null });
  };

  const inRange = (date: string) => start !== null && end !== null && date >= start && date <= end;
  const singlePill = start !== null && end === null; // → now: full pill on the start day
  const singleDate = start !== null && end !== null && start === end;

  return (
    <motion.div
      ref={popRef}
      className="range-cal"
      role="dialog"
      aria-label="Choose a period"
      style={
        {
          left: pos?.left ?? anchor?.left ?? 0,
          top: pos?.top ?? (anchor ? anchor.bottom + GAP : 0),
          "--range-color": color,
        } as CSSProperties
      }
      variants={panel}
      initial="hidden"
      animate="show"
    >
      {header ? <div className="range-cal-header">{header}</div> : null}
      <div className="range-cal-head">
        <button type="button" className="range-cal-nav" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
          <IconChevronLeft />
        </button>
        <div className="range-cal-title" aria-live="polite">
          {MONTHS[view.month]} {view.year}
        </div>
        <button type="button" className="range-cal-nav" aria-label="Next month" onClick={() => shiftMonth(1)}>
          <IconChevronRight />
        </button>
      </div>
      <div className="range-cal-grid">
        {WEEKDAYS.map((w) => (
          <div key={w} className="range-cal-dow">
            {w}
          </div>
        ))}
        {cells.map((c) => {
          const range = inRange(c.date);
          const startCell = c.date === start;
          const endCell = c.date === end;
          // Only the picked day(s) get the pill shape — the single-day (→ now)
          // selection reads as one capsule instead of a half-open bar.
          const pill = (startCell || endCell) && (singlePill || singleDate);
          const dist = start !== null && range ? Math.max(0, daysBetween(start, c.date)) : 0;
          return (
            <button
              key={c.key}
              type="button"
              className={[
                "range-cal-day",
                c.out ? "out" : "",
                range ? "in-range" : "",
                startCell ? "start" : "",
                endCell ? "end" : "",
                pill ? "pill" : "",
                c.date === today ? "today" : "",
              ].filter(Boolean).join(" ")}
              aria-label={fmtCell(c.date)}
              aria-pressed={startCell || endCell}
              style={range || startCell || endCell ? { transitionDelay: `${Math.min(dist, 60) * 16}ms` } : undefined}
              onClick={() => pick(c.date)}
            >
              {c.day}
            </button>
          );
        })}
      </div>
      <div className="range-cal-foot">
        <button type="button" className="range-cal-today" onClick={goToday}>
          Today
        </button>
        {start !== null ? (
          <button type="button" className="range-cal-clear" onClick={() => onChange({ start: null, end: null })}>
            Clear
          </button>
        ) : null}
      </div>
      {actions ? <div className="range-cal-actions">{actions}</div> : null}
    </motion.div>
  );
}
